import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCamera, LuImagePlus, LuLoaderCircle, LuSave, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { useTranslation } from '../../../i18n/LanguageContext';
import { HelpNote } from './ProfileUi';

const MAX_SOURCE_BYTES = 15 * 1024 * 1024;
const MAX_EDGE = 1024;

/**
 * The photo as it will be stored: at most 1024 px on the long edge, as JPEG
 * (the Storage path is `.jpg`) on a white background for transparent PNGs.
 * Falls back to the original file when the browser can't decode it.
 */
export const prepareProfilePhoto = async (file) => {
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = reject;
      element.src = url;
    });
    const scale = Math.min(1, MAX_EDGE / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    return blob || file;
  } catch {
    return file;
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * Preview of a newly chosen profile photo, as it will look on the profile
 * card and in the menu. Nothing is uploaded until the user saves it.
 */
const PhotoUploadDialog = ({ file, saving, onCancel, onConfirm, onPickAnother }) => {
  const { t, language } = useTranslation();
  const label = (key, vars) => t(`profilePage.photo.${key}`, vars);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewFailed, setPreviewFailed] = useState(false);

  useEffect(() => {
    setPreviewFailed(false);
    if (!file) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const notImage = Boolean(file) && !String(file.type || '').startsWith('image/');
  const tooLarge = Boolean(file) && file.size > MAX_SOURCE_BYTES;
  const invalid = notImage || tooLarge;

  const preview = (sizeClass, roundClass) => (
    previewUrl && !previewFailed && !notImage ? (
      <img
        src={previewUrl}
        alt=""
        onError={() => setPreviewFailed(true)}
        className={`${sizeClass} ${roundClass} tw-object-cover tw-shadow-md tw-ring-2 tw-ring-white/90 dark:tw-ring-white/15`}
      />
    ) : (
      <span className={`${sizeClass} ${roundClass} tw-grid tw-place-items-center tw-bg-slate-200 tw-text-slate-400 dark:tw-bg-white/10`}>
        <LuCamera aria-hidden="true" className="tw-h-1/3 tw-w-1/3" />
      </span>
    )
  );

  return (
    <Modal
      isOpen={Boolean(file)}
      onClose={() => !saving && onCancel()}
      size="md"
      title={label('title')}
      subtitle={label('subtitle')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuCamera aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onCancel} disabled={saving} className="max-sm:tw-hidden">{t('profilePage.common.cancel')}</Button>
          <Button onClick={onPickAnother} disabled={saving}>
            <LuImagePlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('another')}
          </Button>
          <Button variant="primary" onClick={() => onConfirm(file)} disabled={saving || invalid} className="max-sm:tw-flex-1">
            {saving ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" /> : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
            {saving ? label('saving') : label('save')}
          </Button>
        </>
      )}
    >
      {file && (
        <div className="tw-space-y-5">
          <div className="tw-flex tw-flex-col tw-items-center tw-gap-5 sm:tw-flex-row sm:tw-items-end sm:tw-justify-center sm:tw-gap-8">
            <figure className="tw-flex tw-flex-col tw-items-center tw-gap-2">
              {preview('tw-h-44 tw-w-44', 'tw-rounded-[2rem]')}
              <figcaption className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('onProfile')}</figcaption>
            </figure>
            <div className="tw-flex tw-items-end tw-gap-6">
              <figure className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                {preview('tw-h-16 tw-w-16', 'tw-rounded-[1.25rem]')}
                <figcaption className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('onCard')}</figcaption>
              </figure>
              <figure className="tw-flex tw-flex-col tw-items-center tw-gap-2">
                {preview('tw-h-9 tw-w-9', 'tw-rounded-full')}
                <figcaption className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{label('inMenu')}</figcaption>
              </figure>
            </div>
          </div>

          <p className="tw-truncate tw-text-center tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
            {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>

          {notImage && <HelpNote tone="danger">{label('notImage')}</HelpNote>}
          {tooLarge && <HelpNote tone="danger">{label('tooLarge')}</HelpNote>}
          {!invalid && previewFailed && (
            <p className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-amber-700 dark:tw-text-amber-300">
              <LuTriangleAlert aria-hidden="true" className="tw-mt-0.5 tw-h-3.5 tw-w-3.5 tw-shrink-0" />
              {label('noPreview')}
            </p>
          )}
          {!invalid && <HelpNote>{label('tips')}</HelpNote>}
        </div>
      )}
    </Modal>
  );
};

PhotoUploadDialog.propTypes = {
  file: PropTypes.object,
  saving: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onPickAnother: PropTypes.func.isRequired,
};

export default PhotoUploadDialog;
